import { graphql, useStaticQuery } from 'gatsby';
import gql from 'graphql-tag';
import React, { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'urql';

import {
  Card,
  CardBody,
  CardCloseButton,
  CardOptions,
  ErrorMessage,
  NakedForm,
  SubmitButton,
  SwitchField,
  useModalContext,
} from '@/components';
import { useTranslate } from '@/contexts';
import { CustomerRestrictionFields } from '@/fragments/CustomerRestriction';
import { useIsMounted } from '@/hooks';
import { Nullable, PlayerId } from '@/types';
import {
  PlayerUpdateMarketingBlockMutation,
  PlayerUpdateMarketingBlockMutationVariables,
} from './__generated__/PlayerUpdateMarketingBlockForm';

const query = graphql`
  query SanityPlayerUpdateMarketingBlockForm {
    sanityPlayerUpdateMarketingBlockForm {
      title {
        ...SanityLocaleString
      }
      description {
        ...SanityLocaleString
      }
      isMarketingBlocked {
        ...SanityLocaleString
      }
      submit {
        ...SanityLocaleString
      }
    }
  }
`;

const updateCustomerMarketingBlockMutation = gql`
  mutation PlayerUpdateMarketingBlock($playerGlobalId: ID!, $enable: Boolean!) {
    updateCustomerMarketingBlockV2(
      playerGlobalId: $playerGlobalId
      enable: $enable
    ) {
      id
      marketingBlock {
        ...CustomerRestrictionFields
      }
    }
  }
  ${CustomerRestrictionFields}
`;

const PlayerUpdateMarketingBlockForm: FC<{
  playerGlobalId: PlayerId;
  isBlocked: boolean;
}> = ({ playerGlobalId, isBlocked }) => {
  const staticData =
    useStaticQuery<Queries.SanityPlayerUpdateMarketingBlockFormQuery>(query);

  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const isMounted = useIsMounted();
  const form = staticData.sanityPlayerUpdateMarketingBlockForm;
  const { close } = useModalContext();
  const { t } = useTranslate();

  const [updateMarketingBlockState, updateMarketingBlock] = useMutation<
    PlayerUpdateMarketingBlockMutation,
    PlayerUpdateMarketingBlockMutationVariables
  >(updateCustomerMarketingBlockMutation);

  const defaultValues = {
    isMarketingBlocked: isBlocked,
  };

  const methods = useForm<typeof defaultValues>({ defaultValues });
  const nextValue = methods.watch('isMarketingBlocked');
  const isUnchanged = nextValue === isBlocked;

  const onSubmit = ({ isMarketingBlocked }: typeof defaultValues) => {
    updateMarketingBlock({
      playerGlobalId,
      enable: isMarketingBlocked,
    }).then((res) => {
      if (res.error?.message && isMounted) {
        setErrorMessage(res.error.message);
      } else if (close) {
        close();
      }
    });
  };

  if (!form) {
    return null;
  }

  return (
    <Card
      size="md"
      title={t(form.title)}
      options={
        <CardOptions>
          <CardCloseButton />
        </CardOptions>
      }
    >
      <CardBody>
        <div className="p-3">
          <NakedForm
            methods={methods}
            onSubmit={onSubmit}
            className="grid gap-6"
          >
            <div className="space-y-1">
              <div className="block text-gray-600 text-sm dark:text-gray-200">
                {t(form.description)}
              </div>
            </div>
            <SwitchField
              title={t(form.isMarketingBlocked)}
              name="isMarketingBlocked"
              labelPlacement="above"
              className="col-span-full"
              allowUndefinedState={false}
            />
            <ErrorMessage message={errorMessage} />
            <SubmitButton
              value={t(form.submit)}
              disabled={updateMarketingBlockState.fetching || isUnchanged}
            />
          </NakedForm>
        </div>
      </CardBody>
    </Card>
  );
};

export default PlayerUpdateMarketingBlockForm;
